<template>
  <div>
    <b-field class="project-search-bar">
      <b-input
        v-model="archiveSearchQuery"
        placeholder="Search"
        icon="magnify"
        pack="mdi"
      >
      </b-input>
    </b-field>
    <p-data-table
      v-if="!dirtyLoading"
      :data="isEmptyArchive ? [] : archiveProjectDetails"
      :hoverable="true"
      :noDataText="`No projects found`"
      :class="$style.table"
    >
      <template slot="thead">
        <tr>
          <td :class="$style.projectTitleCell">Title</td>
          <td :class="$style.projectDateCell">Date Created</td>
          <td :class="$style.projectAdminCell">Administrator</td>
          <td width="1%"></td>
        </tr>
      </template>
      <template slot="tbody" slot-scope="{ data }">
        <tr v-for="item in data" :key="`archived${item.id}`">
          <td :class="$style.projectTitleCell">
            <router-link :to="getProjectUrl(item)">
              {{ item.title }}
            </router-link>
          </td>
          <td :class="$style.projectDateCell">
            {{ formatDate(item.createdAt) }}
          </td>
          <td :class="$style.projectAdminCell">
            <div v-if="item.administratorName.length > 35">
              <b-tooltip
                :label="item.administratorName"
                position="is-top"
                size="is-small"
              >
                <p :class="$style.projectAdminNameCell">
                  {{ item.given_name }} {{ item.family_name }}
                </p>
                <p :class="$style.projectAdminEmailCell">
                  {{
                    item.administratorName.substring(
                      item.given_name.length + item.family_name.length + 2,
                      32,
                    ) + '...'
                  }}
                </p>
              </b-tooltip>
            </div>
            <div v-else>
              <p>
                <b> {{ item.given_name }} {{ item.family_name }} </b> ({{
                  item.user.email
                }})
              </p>
            </div>
          </td>
          <td></td>
        </tr>
      </template>
    </p-data-table>
    <BasePagination
      v-if="!isEmptyArchive && !dirtyLoading"
      :page.sync="getCurrentPage"
      :perPage.sync="getCurrentPerPage"
      :totalNumberOfPage="totalNumberOfPage"
      :totalCount="totalCount"
      :isFirstPage="isFirstPage"
      :isLastPage="isLastPage"
      :startItemIndex="startItemIndex"
      :endItemIndex="endItemIndex"
      class="pagination-container"
    >
    </BasePagination>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import DefaultLayout from '../../layouts/DefaultLayout.vue';
import HeroBar from '@/components/base/HeroBar.vue';
import { Action, State } from 'vuex-class';
import {
  ProjectListPagePayload,
  GetProjectListPayload,
} from '@/store/modules/projects/types/projects.types';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import BasePagination from '@/components/base/BasePagination.vue';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '@/utils/date.util';
import { capitalizeFirstLetter } from '@/jbi-shared/util/user.utils';
import { getProjectUrlByRole } from '@/utils/project.util';

@Component({
  components: {
    DefaultLayout,
    HeroBar,
    BasePagination,
  },
})
export default class ProjectArchiveListPage extends Vue {
  @Prop() public participantProjectOnly!: boolean;
  @Prop() public userEmail!: string;
  @Prop() public isLoading!: boolean;
  public archiveSearchQuery: string = '';
  public totalCount: number = 0;
  public perPage = 50;
  public page = 1;
  public archiveProjectDetails: any = [];
  public isEmptyArchive: boolean = false;
  public dirtyLoading: boolean = false;

  @Action('projects/getArchiveProjects')
  public getArchiveProjects!: (params: GetProjectListPayload) => void;

  @State((state) => state.projects.archiveListProject)
  public archiveListProject!: ProjectListPagePayload[];

  get totalNumberOfPage(): number {
    return Math.ceil(this.totalCount / this.perPage);
  }

  get startItemIndex(): number {
    return this.page * this.perPage - this.perPage + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.totalCount, this.page * this.perPage);
  }

  get isFirstPage(): boolean {
    return this.page === 1;
  }

  get isLastPage(): boolean {
    return this.page === this.totalNumberOfPage;
  }

  get getCurrentPage(): number {
    this.page = +this.$route.query.page || this.page;
    return this.page;
  }

  set getCurrentPage(newValue: number) {
    this.page = newValue;
    this.$router
      .push({
        path: this.$route.path,
        query: {
          search:
            (this.$route.query.search as string) || this.archiveSearchQuery,
          limit: (this.$route.query.limit as string) || this.perPage.toString(),
          page: this.page.toString(),
        },
      })
      .catch(() => {
        return;
      });
    this.getArchiveProjects({
      search: this.archiveSearchQuery,
      limit: this.perPage,
      page: this.page,
      participantProjectOnly: this.participantProjectOnly,
    });
  }

  get getCurrentPerPage(): number {
    this.perPage = +this.$route.query.limit || this.perPage;
    return this.perPage;
  }

  set getCurrentPerPage(newValue: number) {
    this.perPage = newValue;
    this.$router
      .push({
        path: this.$route.path,
        query: {
          search:
            (this.$route.query.search as string) || this.archiveSearchQuery,
          limit: this.perPage.toString(),
          page: (this.$route.query.page as string) || this.page.toString(),
        },
      })
      .catch(() => {
        return;
      });
    this.getArchiveProjects({
      search: this.archiveSearchQuery,
      limit: this.perPage,
      page: this.page,
      participantProjectOnly: this.participantProjectOnly,
    });
  }

  public mounted() {
    this.dirtyLoading = this.isLoading;
    this.archiveSearchQuery = (this.$route.query.search as string) || '';
    if (this.archiveSearchQuery) {
      this.$router
        .push({
          path: this.$route.path,
          query: {
            tab: 'Archive',
            search:
              (this.$route.query.search as string) || this.archiveSearchQuery,
            limit:
              (this.$route.query.limit as string) || this.perPage.toString(),
            page: (this.$route.query.page as string) || this.page.toString(),
          },
        })
        .catch(() => {
          return;
        });
    }
    this.getArchiveProjects({
      search: (this.$route.query.search as string) || this.archiveSearchQuery,
      limit: Number(this.$route.query.limit) || this.perPage,
      page: Number(this.$route.query.page) || this.page,
      participantProjectOnly: this.participantProjectOnly,
    });
  }

  public formatDate(s: string) {
    return dayjs(s).format(DATE_FORMAT);
  }

  public getAdministratorName(item: ProjectListPagePayload) {
    const givenName = capitalizeFirstLetter(item.user.oicPayload.given_name);
    const familyName = capitalizeFirstLetter(item.user.oicPayload.family_name);
    item.administratorName = `${givenName} ${familyName} (${item.user.email})`;
    item.given_name = givenName;
    item.family_name = familyName;
    return item;
  }

  public getProjectUrl(item: any) {
    return getProjectUrlByRole(item.id, item.userRoleInProject);
  }

  @isTruthy
  @Watch('archiveListProject')
  public getWatcherArchiveListProject(newValue: any) {
    this.dirtyLoading = false;
    this.$emit('updateLoadingBool', this.dirtyLoading);
    this.archiveProjectDetails = [];
    if (newValue.items.length > 0) {
      newValue.items.map((dirtyProjectItem: ProjectListPagePayload) => {
        const newProjectItem = this.getAdministratorName(dirtyProjectItem);
        this.archiveProjectDetails.push(newProjectItem);
      });
      this.isEmptyArchive = false;
    } else {
      this.isEmptyArchive = true;
    }
    this.totalCount = newValue.totalItems;
  }

  @Watch('archiveSearchQuery')
  public watchArchiveSearchQuery(newValue: string) {
    this.page = 1;
    this.$router
      .push({
        path: this.$route.path,
        query: {
          tab: 'Archive',
          search: newValue,
          limit: this.perPage.toString(),
          page: this.page.toString(),
        },
      })
      .catch(() => {
        return;
      });
    this.getArchiveProjects({
      search: newValue,
      limit: this.perPage,
      page: this.page,
      participantProjectOnly: this.participantProjectOnly,
    });
  }

  @Watch('$route.query.tab')
  public routeParam() {
    if (this.$route.query.tab === 'Archive') {
      this.$router
        .push({
          path: this.$route.path,
          query: {
            tab: 'Archive',
            search: this.archiveSearchQuery,
            limit: this.perPage.toString(),
            page: this.page.toString(),
          },
        })
        .catch(() => {
          return;
        });
      const params = {
        search: this.archiveSearchQuery,
        limit: this.perPage,
        page: this.page,
        participantProjectOnly: this.participantProjectOnly,
      };

      return this.getArchiveProjects(params);
    }
  }
}
</script>

<style lang="scss" module>
.heroBar {
  margin-right: 16px;
  margin-left: 16px;
}
.projectTitleCell {
  width: 60%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.projectTitleDraftCell {
  width: 80%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.projectDateCell {
  width: 10%;
}
.projectAdminCell {
  width: 25%;
  overflow: visible;
  text-align: left;
}
.projectCreatedByMeCell {
  width: 55%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.projectCreatedByAdminCell {
  width: 30%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow: visible;
}
.projectAdminNameCell {
  font-weight: 700;
  display: inline-block;
}
.projectAdminEmailCell {
  display: inline-block;
  padding-left: 5px;
}
.tooltipTitle {
  font-family: Oswald;
  font-size: 40px;
  font-weight: 700;
  color: #00205b;
  &::after {
    font-family: 'Open Sans';
  }
}
.table {
  table {
    table-layout: fixed;
  }
}
.b-too .dropdown :global {
  .dropdown-menu {
    .dropdown-content {
      padding: 0;

      .dropdown-item {
        border-bottom: 1px solid lightgrey;
        font-size: 11px;
        color: #444f51;
        text-decoration: none;

        &:hover,
        label {
          background: #f7fbff;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
